import { useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import PayeeList from '#client/components/PayeesModal/components/PayeeList';
import { COUNTER_PARTY_STATUS } from '#client/constants/payees';
import { TOKEN_TYPES } from '#client/constants/tokens';
import useBankingAccounts from '#client/hooks/useBankingAccounts';
import useBankingPath from '#client/hooks/useBankingPath';
import useCustomerToken from '#client/hooks/useCustomerToken';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import useSkeleton from '#client/hooks/useSkeleton';
import useToastContext from '#client/hooks/useToastContext';
import { UPDATE_COUNTER_PARTY_STATUS_MUTATION } from '#client/mutations';

import EmptyState from './EmptyState';
import ListItemSkeleton from './ListItemSkeleton';
import SectionLink from './SectionLink';

export default function Payees({
  accounts,
  data = null,
  loading = false,
  addPayeeHandler,
  refetch
}) {
  const captureException = useCaptureException();
  const { isDisable } = useDisableDashboardContext();

  const { createSuccessToast, createErrorToast } = useToastContext();
  const bankingPath = useBankingPath();

  const { getCustomerToken } = useCustomerToken({ type: TOKEN_TYPES.COMMON });

  const { id: bankingAccountId, loading: isFetchingBankingAccounts } = useBankingAccounts();

  const [showSkeleton] = useSkeleton(
    !isFetchingBankingAccounts && !!bankingAccountId && !loading && !!data
  );

  // mutations
  const [updateCounterParty, { loading: loadingUpdatePayeeStatus }] = useMutation(
    UPDATE_COUNTER_PARTY_STATUS_MUTATION,
    {
      noThrow: true
    }
  );

  const handleUpdatePayeeStatus = useCallback(
    async ({ id, name, status }) => {
      try {
        // Retrieve customer token
        const customerToken = await getCustomerToken();

        // If there's no customer token, close the modal and return
        if (!customerToken) {
          return;
        }
        // Update the payee status
        const { error: updatePayeeError } = await updateCounterParty({
          variables: {
            input: {
              counterpartyId: id,
              status,
              customerToken,
              bankingAccountId,
              idempotencyKey: uuidv4()
            }
          },
          skip: loadingUpdatePayeeStatus
        });

        // Handle errors and show a toast
        if (updatePayeeError) {
          // Show toast error
          createErrorToast({
            title: 'Payee was not updated',
            description: 'There was an error updating this payee. Please try again.',
            error: updatePayeeError
          });
        } else {
          // Show success toast and close modal
          createSuccessToast({
            title: `${name} has been ${
              status === COUNTER_PARTY_STATUS.ARCHIVED ? 'archived' : 'unarchived'
            }`,
            description: `${
              status === COUNTER_PARTY_STATUS.ARCHIVED
                ? `You can still access your transaction history with ${name}`
                : `You can send money to ${name} again`
            }`
          });

          // Reset pagination after successful update
          refetch();
        }
      } catch (error) {
        // Handle unexpected errors
        captureException(new Error(`Error while updating payee status with ID: ${id}`));
      }
    },
    [
      refetch,
      captureException,
      getCustomerToken,
      createErrorToast,
      createSuccessToast,
      updateCounterParty,
      loadingUpdatePayeeStatus,
      bankingAccountId
    ]
  );

  return (
    <Stack>
      {showSkeleton ? (
        <ListItemSkeleton />
      ) : (
        <>
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          >
            <Text
              size="lg"
              weight="medium"
            >
              Payees
            </Text>
            <SectionLink
              isDisable={isDisable}
              text="See all"
              trackingProperties={{
                elementName: 'payees_see_all'
              }}
              url={`${bankingPath}/payees`}
            />
          </Box>
          <Box>
            {accounts && accounts.length > 0 ? (
              <PayeeList
                handleDeleteSuccess={refetch}
                handleUpdatePayeeStatus={handleUpdatePayeeStatus}
                loadingAccounts={loading}
                payees={accounts}
                usedAt="dashboard"
              />
            ) : (
              <Box
                p="16px 0px"
                textAlign="center"
              >
                <EmptyState
                  onClick={addPayeeHandler}
                  text="Add a payee"
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Stack>
  );
}

Payees.propTypes = {
  loading: PropTypes.bool,
  data: PropTypes.shape({}),
  refetch: PropTypes.func.isRequired,
  addPayeeHandler: PropTypes.func.isRequired,
  accounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};
